// reducers/contacts.reducer.ts
import { Action, createReducer, on } from '@ngrx/store';

import { createEntityAdapter, EntityState } from '@ngrx/entity';
import {
  NewContactActions,
  ContactsActions,
  UpdateContactActions,
  DeleteContactActions,
} from './contacts.actions';
import { SessionActions } from '../user/auth/auth.actions';
import { ContactItem } from 'src/app/components/user-dashboard/contacts/models/contacts';
import { SystemProcessLoadingStatus } from 'src/app/models/global-classes';

export interface ContactsState extends EntityState<ContactItem> {
  loaded: boolean;
  error: any;

  createNewContactProcess: {
    status: SystemProcessLoadingStatus;
    error: any;
    lastCreatedContact?: ContactItem;
  };
  updateContactProcess: {
    status: SystemProcessLoadingStatus;
    error: any;
    updatedContact?: any;
  };
}

export const contactsAdapter = createEntityAdapter<ContactItem>();

const initialState: ContactsState = contactsAdapter.getInitialState({
  loaded: false,
  error: null,

  createNewContactProcess: { status: 'none', error: null },
  updateContactProcess: { status: 'none', error: null },
});

export const contactsReducer = createReducer(
  initialState,
  on(ContactsActions.load, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(ContactsActions.loadSuccess, (state, { contacts }) => {
    return contactsAdapter.setAll(contacts, { ...state, loaded: true });
  }),
  on(ContactsActions.loadFailure, (state, { error }) => {
    return { ...state, error, loaded: false };
  }),

  // Handle Create Contact
  on(NewContactActions.create, (state) => ({
    ...state,
    createNewContactProcess: { status: 'creating', error: null },
  })),
  on(NewContactActions.loadSuccess, (state, { contact }) => {
    return contactsAdapter.addOne(contact, {
      ...state,
      createNewContactProcess: {
        status: 'done',
        error: null,
        lastCreatedContact: contact,
      },
    });
  }),
  on(NewContactActions.loadFailure, (state, { error }) => {
    return { ...state, createNewContactProcess: { status: 'error', error } };
  }),

  // Handle Update Contact
  on(UpdateContactActions.update, (state) => ({
    ...state,
    updateContactProcess: { status: 'updating', error: null },
  })),
  on(UpdateContactActions.updateSuccess, (state, { contact }) => {
    return contactsAdapter.upsertOne(contact, {
      ...state,
      updateContactProcess: {
        status: 'done',
        error: null,
        updatedContact: contact,
      },
    });
  }),
  on(UpdateContactActions.updateFailure, (state, { error }) => {
    return { ...state, updateContactProcess: { status: 'error', error } };
  }),

  // Handle Delete Contact
  on(DeleteContactActions.deleteSuccess, (state, { id }) => {
    return contactsAdapter.removeOne(id, state);
  }),

  on(SessionActions.forget, (state) => initialState),
);
